"use client";

import Image from '@/components/ui/Image';
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import React from "react";
import { ButtonStyleType, SpacingType, sectionVariants } from "@/types";
import { Badge } from "@/components/ui/badge";
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";

interface Hero2Props {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  contents?: {
    badge?: string;
    title?: string;
    content?: string;
    hasButton?: boolean;
    buttonText?: string;
    buttonLink?: string;
    buttonStyle?: ButtonStyleType;
    secondaryButton?: boolean;
    secondaryButtonText?: string;
    secondaryButtonLink?: string;
    secondaryButtonStyle?: ButtonStyleType;
    image?: string;
    altText?: string;
    microCopy?: string;
  };
  hasStats?: boolean;
  statsAtBottom?: boolean;
  stats?: {
    value?: string;
    label?: string;
  }[];
  attributes?: React.HTMLProps<HTMLElement>;
}

const Hero2 = ({
  darkMode = false,
  spacing,
  variant = 'A',
  contents,
  hasStats,
  statsAtBottom,
  stats,
  attributes,
}: Hero2Props) => {
  const statsClass = hasStats && statsAtBottom ? 'mt-20 xl:mt-40' : 'mt-12 xl:mt-20';

  return (
    <SectionWrapper
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={true}
      componentName="hero2"
      attributes={attributes}
      usedInPageNav={false}
      pageNavTitle="Hero 2"
      className={`${hasStats && statsAtBottom ? 'sm:!pb-8' : ''}`}
    > 
      <div className="container">
        {/* {contents?.badge && (
          <div className="flex w-fit items-center rounded-full border px-3 py-1 paragraph-sm mb-8 md:mb-10">
            {contents?.badge}
          </div>
        )} */}
        <div className="flex flex-col xl:flex-row gap-x-20 items-center">
          <div className="xl:w-[60%]">
            {contents?.badge && (
              <Badge variant="outline" bold className="mb-6">
                {contents?.badge}
              </Badge>
            )}
            {contents?.title && (
              <>
              <h1 className="mb-8 heading-2">
                {parse(contents.title)}
              </h1>
              </>
            )}
            {contents?.content && (
              <div className="paragraph-lg mx-auto !font-normal">
                {parse(contents?.content)}
              </div>
            )}
            {contents?.hasButton && (
              <div className="mt-8 flex flex-col ">
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button 
                    size="lg" 
                    variant={contents?.buttonStyle || "primary"}
                    className="w-full gap-2 sm:w-auto"
                    asChild
                  >
                    <a href={contents?.buttonLink}>
                      {contents?.buttonText}
                    </a>
                  </Button>
                  {contents?.secondaryButton && (
                    <Button
                      variant={contents?.secondaryButtonStyle || "outline"}
                      size="lg"
                      className="w-full gap-2 sm:w-auto"
                      asChild
                    >
                      <a href={contents?.secondaryButtonLink}>
                        {contents?.secondaryButtonText}
                      </a>
                    </Button>
                  )}
                </div>
                {contents?.microCopy && (
                  <p className="paragraph-sm text-muted-foreground text-center md:text-left mt-3">
                    {contents?.microCopy}
                  </p>
                )}  
              </div>
            )}
          </div>
          {contents?.image && (
            <div className="relative w-full xl:w-[40%] mt-8 xl:mt-0">
              {/* <div className="absolute top-4 left-4 w-[calc(100%-1rem)] h-[calc(100%-1rem)] rounded-md bg-[image:var(--color-gradient-primary)] z-0"></div> */}
              <div className="absolute top-0 left-0 w-full h-full">
                <Image src="/gradient-mask.svg" alt="gradient" width={505} height={285} className="aspect-[505/285] h-full object-fill relative w-full rounded-md z-10" priority fetchPriority="high" />
              </div>
              <Image 
                src={contents?.image} 
                alt={contents?.altText || 'placeholder'} 
                title={contents?.altText || 'placeholder'} 
                width={505} 
                height={285} 
                className="aspect-[505/285] xl:h-full object-cover relative z-1 w-full rounded-md" 
                priority
                fetchPriority="high"
              />
            </div>
          )}
        </div>
      </div>
      {hasStats && stats && (
        <div className={`${statsClass} flex flex-col md:flex-row justify-start gap-2 md:gap-10 container`}>
          {stats.map((stat, index) => (
            <React.Fragment key={index}>
              <div className="flex flex-row md:flex-col items-center md:items-start justify-between gap-1">
                {stat.value && (
                  <h4 className="text-gradient-primary">
                    {stat.value}
                  </h4>
                )}
                {stat.label && (
                  <p className="text-foreground">{stat.label}</p>
                )}
              </div>
              {index < stats.length - 1 && (
                <React.Fragment>
                  <Separator orientation="horizontal" className="block md:hidden" />
                  <Separator orientation="vertical" className="!h-auto hidden md:block" />
                </React.Fragment>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </SectionWrapper>
  );
};

export { Hero2 };
